import { supportedLocales, type SupportedLocale } from '@org/i18n';

/** The slice of `users.getUserByEmail` the on-screen language rule looks at. */
export type LocaleProfile = {
  uiLanguage?: string | null;
  language?: string | null;
  isStudent?: boolean | null;
  levelName?: string | null;
};

export const DEFAULT_LOCALE: SupportedLocale = 'en';

export const isSupportedLocale = (locale?: string | null): locale is SupportedLocale =>
  Boolean(locale && (supportedLocales as string[]).includes(locale));

/** CEFR band letter of a level name such as "A1.2" or "B2.3" — undefined when unknown. */
const cefrBand = (levelName?: string | null) => {
  const letter = levelName?.trim().charAt(0).toUpperCase();
  return letter && /^[ABC]$/.test(letter) ? letter : undefined;
};

/**
 * Which language the apps show when the user opens the platform.
 *
 *   1. A language picked in the sidebar toggle (`uiLanguage`) always wins.
 *   2. Otherwise, students at A levels start in Portuguese; students at B or C
 *      levels start in English (the platform itself is their English practice).
 *   3. Everyone else (no level yet, teachers, community members) follows their
 *      profile language, which is Portuguese for this school.
 */
export const resolveInitialLocale = (profile?: LocaleProfile | null): SupportedLocale => {
  if (isSupportedLocale(profile?.uiLanguage)) return profile.uiLanguage;

  if (profile?.isStudent) {
    const band = cefrBand(profile.levelName);
    if (band === 'A') return 'pt';
    if (band === 'B' || band === 'C') return 'en';
  }

  return isSupportedLocale(profile?.language) ? profile.language : DEFAULT_LOCALE;
};
